import { Component, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { filter, switchMap } from 'rxjs/operators';
import {
  Exam,
  LiveEvent,
  QuestionModel,
  QuizModel,
  QuizQuery,
} from 'src/app/api/models';
import {
  ExamsService,
  LiveEventsService,
  QuizzesService,
} from 'src/app/api/services';
import { DialogService } from 'src/app/cms/common/dialog.service';
import { ListActionMeta, ListConfig, ListQuery } from './list.types';
import { TranslateService } from '@ngx-translate/core';
import { ActivatedRoute, Router } from '@angular/router';
import {
  getQuizImports,
  getWizardQuizImports,
  QuizPanel,
} from '../panel/panels/quiz-panel';
import { PanelService } from '../panel/panel.service';
import { Panel, PanelEntity, PanelEntityType } from '../panel/panel.types';
import * as fileSaver from 'file-saver';
import { cloneDeep } from 'lodash';
import {
  multiSelectAction,
  GridDataComponent,
} from './grid-data/grid-data.component';
import {
  ConfirmConvertFromQuizComponent,
  ShowInfoItem,
} from '../common/dialogs/confirm-convert-from-quiz/confirm-convert-from-quiz.component';
import { EditService } from '../edit/edit.service';
import { QuizEditConfigService } from '../edit/configs/quiz.service';
import { XLSXReader } from '../common/import-helpers/xlsxReader';
import {
  validateSchema,
  INFO_DIALOG_WIDTH,
  settingAnswers,
  settingData,
  validateQuestionAnswers,
  validateQuestions,
} from 'src/app/utility/app.utils';
import {
  AmplitudeSource,
  SourceType,
  UIEventEnum,
} from 'src/app/utility/amplitude-tracking/tracking.types';
import { firstValueFrom, of } from 'rxjs';
import { schemas } from 'src/app/payload-validators';
import { QuestionType } from 'src/app/app.types';
import { QuestionPanel } from '../panel/panels/question-panel';
import { TrackingService } from 'src/app/utility/amplitude-tracking/tracking.service';
import { UtilityService } from 'src/app/utility/utility.service';
import { AIImportDialogComponentData } from '../common/dialogs/ai-import-dialog/ai-import-dialog.component';

@Component({
  selector: 'qa-quiz-list',
  templateUrl: './quiz-list.component.html',
  styleUrls: ['./quiz-list.component.scss'],
  imports: [GridDataComponent],
})
export class QuizListComponent implements OnInit {
  config: ListConfig<QuizModel>;
  navGroups = [];
  constructor(
    private quizzes: QuizzesService,
    private panelService: PanelService,
    private dialog: DialogService,
    private matDialog: MatDialog,
    private matSnackBar: MatSnackBar,
    private translator: TranslateService,
    private router: Router,
    private exams: ExamsService,
    private events: LiveEventsService,
    private editService: EditService,
    private trackingService: TrackingService,
    private utilityService: UtilityService,
    quizPanel: QuizPanel,
    questionsPanel: QuestionPanel,
    route: ActivatedRoute
  ) {
    if (!panelService?.panels.length) {
      panelService.init([quizPanel, questionsPanel], route);
    }
  }

  ngOnInit() {
    const quizPanelEntity = this.panelService.panels.find(
      (panel) => panel.type === PanelEntityType.Quiz
    );
    const config = getQuizListConfig(
      this.quizzes,
      this.matDialog,
      this.dialog,
      this.matSnackBar,
      this.translator,
      true,
      this.router,
      this.editService,
      this.exams,
      this.events,
      this.panelService,
      quizPanelEntity,
      this.trackingService,
      this.utilityService
    );
    this.setImports(config);
    this.setWizardImports(config);
    this.config = config;
  }

  /* TODO: 
    It used in books module which has old import options design.
    so this setImports() will be removed in future.
  */
  setImports(config) {
    const imports = getQuizImports(this.translator, null, null, false);
    imports[0].action.click = async (dataSource) => {
      const item = (await this.panelService.promptFile('json')) as QuizModel;
      this.matDialog.closeAll();
      dataSource.loading = true;
      const sentryInfo = {
        title: `Error while importing quiz from JSON`,
        file: 'quiz-list.component.ts - import from JSON',
        api: 'createQuiz',
        preventLogs: true,
      };
      const { isInvalidSchema } = validateSchema(
        schemas.QuizModel,
        item,
        this.matSnackBar,
        sentryInfo
      );
      if (isInvalidSchema) {
        dataSource.loading = false;
        return;
      }
      const validateAnswers = validateQuestionAnswers(
        item?.questions,
        this.translator
      );
      if (validateAnswers?.hasError) {
        this.matSnackBar.open(validateAnswers.error);
        return;
      }
      this.quizzes
        .createQuiz({
          body: item,
          source: SourceType.JSON_IMPORT,
        })
        .subscribe(
          (quiz) => {
            const sentryInfo = {
              title: `Error while importing quiz questions from JSON`,
              file: 'quiz-list.component.ts - import from JSON',
              api: 'copyQuestionToQuiz',
              preventLogs: true,
            };
            this.importQuestions(
              item?.questions,
              quiz.id,
              dataSource,
              sentryInfo
            );
          },
          () => (dataSource.loading = false)
        );
    };
    imports[1].action.click = async (dataSource) => {
      const item = (await this.panelService.getJSONFromXML()) as QuizModel;
      this.matDialog.closeAll();
      dataSource.loading = true;
      const sentryInfo = {
        title: `Error while importing quiz from XML`,
        file: 'quiz-list.component.ts - import from XML',
        api: 'createQuiz',
      };
      const { isInvalidSchema } = validateSchema(
        schemas.QuizModel,
        item,
        this.matSnackBar,
        sentryInfo
      );
      if (isInvalidSchema) {
        dataSource.loading = false;
        return;
      }
      this.quizzes
        .createQuiz({
          body: item,
          source: SourceType.XML_IMPORT,
        })
        .subscribe(
          (quiz) => {
            const sentryInfo = {
              title: `Error while importing quiz questions from XML`,
              file: 'quiz-list.component.ts - import from XML',
              api: 'copyQuestionToQuiz',
              preventLogs: true,
            };
            this.importQuestions(
              item?.questions,
              quiz.id,
              dataSource,
              sentryInfo
            );
            this.matSnackBar.open(
              this.translator.instant(
                'content.quiz.importFromExcel.quizCreatedSuccessfully'
              )
            );
          },
          () => {
            dataSource.loading = false;
          }
        );
    };
    imports.splice(1, 0, {
      title: this.translator.instant('content.quiz.importfromKahootTitle'),
      description: this.translator.instant('content.quiz.importFromKahootDesc'),
      action: {
        text: this.translator.instant('content.quiz.importFromKahoot'),
        icon: 'description',
        click: () => {
          this.importFromKahoot();
        },
      },
      extra: {
        name: this.translator.instant('content.quiz.extra'),
        type: 'link',
        icon: 'keyboard_arrow_right',
        click: () => {
          window.open(
            'https://quizacademy.de/tipps/kahoot-inhalte-importieren/',
            '_blank'
          );
        },
      },
    });
    config.imports = imports;
  }

  setWizardImports(config) {
    const wizardImports = getWizardQuizImports(
      this.translator,
      null,
      null,
      false
    );
    wizardImports.createButton.click = async (dataSource) => {
      await this.editService.open(QuizEditConfigService);
      dataSource?.refresh();
      this.matDialog.closeAll();
    };
    wizardImports.createWithAI.click = async (dataSource) => {
      const quiz = await this.editService.open(QuizEditConfigService);
      dataSource?.refresh();
      if (quiz && quiz?.id) {
        this.router
          .navigateByUrl(`/cms/content/${quiz.id}/questions`)
          .then(() => {
            this.matDialog?.closeAll();
            const dialogData: AIImportDialogComponentData = {
              name: null,
              type: null,
              onImport: () => {
                const queryParams = {
                  type: PanelEntityType.Question,
                };
                this.panelService.redirectToImportItems(
                  `/cms/csv-imports/question/${quiz.id}`,
                  queryParams,
                  true
                );
                this.matDialog?.closeAll();
              },
            };
            this.panelService.openChatGprDialog(dialogData);
          });
      }
      this.matDialog.closeAll();
    };
    const idxJson = wizardImports.importButtons.findIndex(
      (imp) => imp.id === 'import-json'
    );
    if (idxJson !== -1) {
      wizardImports.importButtons[idxJson].click = async (dataSource) => {
        const item = (await this.panelService.promptFile('json')) as QuizModel;
        this.matDialog.closeAll();
        dataSource.loading = true;
        const sentryInfo = {
          title: `Error while importing quiz from JSON`,
          file: 'quiz-list.component.ts - import from JSON',
          api: 'createQuiz',
          preventLogs: true,
        };
        const { isInvalidSchema } = validateSchema(
          schemas.QuizModel,
          item,
          this.matSnackBar,
          sentryInfo
        );
        if (isInvalidSchema) {
          dataSource.loading = false;
          return;
        }
        const validateAnswers = validateQuestionAnswers(
          item?.questions,
          this.translator
        );
        if (validateAnswers?.hasError) {
          this.matSnackBar.open(validateAnswers.error);
          return;
        }
        this.quizzes
          .createQuiz({
            body: item,
            source: SourceType.JSON_IMPORT,
          })
          .subscribe(
            (quiz) => {
              const sentryInfo = {
                title: `Error while importing quiz questions from JSON`,
                file: 'quiz-list.component.ts - import from JSON',
                api: 'copyQuestionToQuiz',
                preventLogs: true,
              };
              this.importQuestions(
                item?.questions,
                quiz.id,
                dataSource,
                sentryInfo
              );
            },
            () => (dataSource.loading = false)
          );
      };
    }
    const idxXml = wizardImports.importButtons.findIndex(
      (imp) => imp.id === 'import-xml'
    );
    if (idxXml !== -1) {
      wizardImports.importButtons[idxXml].click = async (dataSource) => {
        const item = (await this.panelService.getJSONFromXML()) as QuizModel;
        this.matDialog.closeAll();
        dataSource.loading = true;
        const sentryInfo = {
          title: `Error while importing quiz from XML`,
          file: 'quiz-list.component.ts - import from XML',
          api: 'createQuiz',
        };
        const { isInvalidSchema } = validateSchema(
          schemas.QuizModel,
          item,
          this.matSnackBar,
          sentryInfo
        );
        if (isInvalidSchema) {
          dataSource.loading = false;
          return;
        }
        this.quizzes
          .createQuiz({
            body: item,
            source: SourceType.XML_IMPORT,
          })
          .subscribe(
            (quiz) => {
              const sentryInfo = {
                title: `Error while importing quiz questions from XML`,
                file: 'quiz-list.component.ts - import from XML',
                api: 'copyQuestionToQuiz',
                preventLogs: true,
              };
              this.importQuestions(
                item?.questions,
                quiz.id,
                dataSource,
                sentryInfo
              );
              this.matSnackBar.open(
                this.translator.instant(
                  'content.quiz.importFromExcel.quizCreatedSuccessfully'
                )
              );
            },
            () => {
              dataSource.loading = false;
            }
          );
      };
    }
    const idxKahoot = wizardImports.importButtons.findIndex(
      (imp) => imp.id === 'import-kahoot'
    );
    if (idxKahoot !== -1) {
      wizardImports.importButtons[idxKahoot].click = () =>
        this.importFromKahoot();
    }
    const idxCsv = wizardImports.importButtons.findIndex(
      (imp) => imp.id === 'import-csv'
    );
    if (idxCsv !== -1) {
      wizardImports.importButtons[idxCsv].click = async (dataSource) => {
        const quiz = await this.editService.open(QuizEditConfigService);
        dataSource?.refresh();
        if (quiz && quiz?.id) {
          this.router
            .navigateByUrl(`/cms/content/${quiz.id}/questions`)
            .then(() => {
              const queryParams = {
                type: PanelEntityType.Question,
              };
              this.panelService.redirectToImportItems(
                `/cms/csv-imports/question/${quiz.id}`,
                queryParams,
                true
              );
              this.matDialog?.closeAll();
            });
        }
      };
    }
    config.wizardImports = wizardImports;
  }

  async importFromKahoot() {
    const excelFile = await this.panelService.selectExcelFileAsBinary();
    const file = XLSXReader.excelToArray(excelFile);
    const name = file[0][0];
    if (!name || !name.trim()) {
      this.matSnackBar.open(
        this.translator.instant(
          'content.quiz.importFromExcel.provideNameForQuiz'
        )
      );
      return;
    }
    let quiz;
    const snackbar = this.matSnackBar.open(
      this.translator.instant('content.quiz.importFromExcel.createQuiz')
    );
    const questions = this.panelService.importQuestionsFromKahoot(excelFile);
    let questionArr = settingData(questions);
    settingAnswers(questionArr);
    questionArr.forEach((question) => {
      if (question['type'] === 'True/False') {
        question['type'] = QuestionType.TRUE_FALSE;
        if (question['answers'].length && question['answers'][0]['is_right']) {
          question['is_right'] = question['answers'][0]['is_right'];
        }
        delete question['answers'];
      } else if (question['type'] === 'Multiple Choice') {
        question['type'] = QuestionType.MULTIPLE_CHOICE;
      } else if (question['type'] === 'Single Choice') {
        question['type'] = QuestionType.SINGLE_CHOICE;
      }
    });
    questionArr = questionArr.filter(
      (q) =>
        q.type === QuestionType.TRUE_FALSE ||
        (q.type !== QuestionType.TRUE_FALSE && q.answers?.length)
    );
    const hasFreeTextQuestion = questionArr.some(
      (q) => q.type === QuestionType.FREETEXT
    );
    if (hasFreeTextQuestion) {
      this.matSnackBar.open(
        this.translator.instant('quiz.message.freeTextNotAllowed')
      );
      return;
    }
    if (name) {
      const sentryInfo = {
        title: `Error while importing quiz from Kahoot`,
        file: 'quiz-list.component.ts - import from Kahoot',
        api: 'createQuiz',
        preventLogs: true,
      };
      const { isInvalidSchema } = validateSchema(
        schemas.QuizModel,
        { name },
        this.matSnackBar,
        sentryInfo
      );
      if (isInvalidSchema) {
        return;
      }
      quiz = await this.quizzes
        .createQuiz({
          body: {
            name: name,
          } as QuizModel,
          source: SourceType.KAHOOT,
        })
        .toPromise();
      this.utilityService.triggerRefresh();
      this.matSnackBar.open(
        this.translator.instant(
          'content.quiz.importFromExcel.addQuestionsToQuiz'
        )
      );
      const sentryQuestionInfo = {
        title: `Error while importing quiz questions from Kahoot`,
        file: 'quiz-list.component.ts - import from Kahoot',
        api: 'copyQuestionToQuiz',
        preventLogs: true,
      };
      const { isValid } = validateQuestions(
        sentryQuestionInfo,
        questionArr,
        this.translator,
        this.matSnackBar
      );
      if (!isValid) {
        return;
      }
      await this.quizzes
        .copyQuestionToQuiz({
          id: quiz.id,
          body: questionArr,
        })
        .toPromise();
      snackbar.dismiss();
      this.matSnackBar.open(
        this.translator.instant(
          'content.quiz.importFromExcel.quizCreatedSuccessfully'
        )
      );
      this.matDialog.closeAll();
      this.router.navigate(['cms', 'content', quiz.id, 'questions']);
    }
  }

  async importQuestions(
    items: QuestionModel[],
    parentId: number,
    dataSource,
    sentryInfo
  ) {
    if (items?.length) {
      const hasFreeTextQuestion = items.some(
        (q) => q.type === QuestionType.FREETEXT
      );
      if (hasFreeTextQuestion) {
        this.matSnackBar.open(
          this.translator.instant('quiz.message.freeTextNotAllowed')
        );
        return;
      }
      const { isValid } = validateQuestions(
        sentryInfo,
        items,
        this.translator,
        this.matSnackBar
      );
      if (!isValid) {
        return;
      }
      await this.quizzes
        .copyQuestionToQuiz({ id: parentId, body: items })
        .toPromise();
      this.utilityService.triggerRefresh();
    }
    dataSource.refresh();
  }
}

export function getQuizListConfig(
  quizzes: QuizzesService,
  matDialog: MatDialog,
  dialog: DialogService,
  matSnackBar: MatSnackBar,
  translator: TranslateService,
  showMultiselect: boolean,
  router: Router,
  editService: EditService,
  exams?: ExamsService,
  events?: LiveEventsService,
  panelService?: PanelService,
  quizPanel?: Panel<PanelEntity>,
  trackingService?: TrackingService,
  utilityService?: UtilityService
): ListConfig<QuizModel> {
  async function exportAsJson(meta: ListActionMeta<QuizModel>) {
    meta.dataSource.loading = true;
    let fullItem: QuizModel;
    fullItem = cloneDeep(meta.row);
    const questions = await quizzes
      .getQuestionsForQuiz({
        id: fullItem.id,
        embed_answers: true,
        embed_tags: true,
      })
      .toPromise();
    questions.forEach((q) => delete q.id);
    delete fullItem.id;
    fullItem.questions = questions;
    fullItem['meta:importType'] = PanelEntityType.Quiz;
    const file = `${JSON.stringify(fullItem)}\n`;
    const blob = new Blob([file], { type: 'application/json' });
    fileSaver.saveAs(blob, `qa-${PanelEntityType.Quiz}-${fullItem.name}.json`);
    trackingService.trackEvent({
      event_type: UIEventEnum.JSON_EXPORTED,
      event_properties: {
        source: AmplitudeSource.QUIZ,
      },
    });
    meta.dataSource.loading = false;
  }
  const ret: ListConfig<QuizModel> = {
    actions: [
      {
        click: ({ row }) => {
          router.navigate([`/cms/content/${row.id}/questions`], {
            queryParams: { showStatistics: true },
          });
        },
        icon: 'timeline',
        name: 'stats',
        label: 'quiz.tooltip.statistics',
        tooltip: 'content.quiz.actionMenu.openStatistics',
        type: 'button',
      },
      {
        click: ({ row, dataSource }) => {
          dialog
            .confirmDelete()
            .pipe(
              filter((isConfirmed) => isConfirmed),
              switchMap(() => quizzes.deleteQuiz({ id: row.id }))
            )
            .subscribe({
              next: () => {
                matSnackBar.open(
                  translator.instant('content.quiz.deleteItem.success')
                );
                dataSource.refresh();
                utilityService?.triggerRefresh();
              },
              error: () => {
                matSnackBar.open(
                  translator.instant('content.quiz.deleteItem.error')
                );
              },
            });
        },
        icon: 'delete',
        name: 'delete',
        tooltip: 'quiz.tooltip.delete',
        label: 'quiz.tooltip.delete',
        type: 'button',
      },
    ],
    actionsMenu: [
      {
        icon: 'question_answer',
        name: 'edit',
        label: translator.instant('liveEvent.setup.label.manageQuestions'),
        type: 'button',
        click: (meta) => {
          router.navigateByUrl(
            `/cms/content/${meta.row.id}/questions?from=quiz`
          );
        },
      },
      {
        icon: 'play_arrow',
        name: 'convert',
        label: translator.instant('content.quiz.actionMenu.convertToLiveEvent'),
        type: 'button',
        click: (meta) => {
          const dialogRef = matDialog.open<
            ConfirmConvertFromQuizComponent,
            ShowInfoItem
          >(ConfirmConvertFromQuizComponent, {
            data: {
              modelTitle: translator.instant(
                'course.confirmAction.convertContentQuizToEvent'
              ),
              confirmButtonText: translator.instant(
                'course.confirmAction.convertToEvent'
              ),
            },
            width: INFO_DIALOG_WIDTH,
            height: 'auto',
            autoFocus: false,
            panelClass: 'confirm-convert-quiz-modal',
          });
          dialogRef.afterClosed().subscribe((resp) => {
            if (resp) {
              convertQuizToEvent(
                meta.row,
                quizzes,
                events,
                matSnackBar,
                translator,
                router
              );
            }
          });
        },
      },
      {
        icon: 'transform',
        name: 'convert',
        label: translator.instant('content.quiz.actionMenu.convertToExam'),
        type: 'button',
        click: (meta) => {
          const dialogRef = matDialog.open<
            ConfirmConvertFromQuizComponent,
            ShowInfoItem
          >(ConfirmConvertFromQuizComponent, {
            data: {
              modelTitle: translator.instant(
                'course.confirmAction.convertContentQuizToExam'
              ),
              confirmButtonText: translator.instant(
                'course.confirmAction.convertToExam'
              ),
            },
            width: INFO_DIALOG_WIDTH,
            height: 'auto',
            autoFocus: false,
            panelClass: 'confirm-convert-quiz-modal',
          });
          dialogRef.afterClosed().subscribe((resp) => {
            if (resp) {
              convertToExam(
                meta.row,
                quizzes,
                exams,
                matSnackBar,
                translator,
                router
              );
            }
          });
        },
      },
      {
        icon: 'save_alt',
        name: 'download',
        label: translator.instant('content.quiz.actionMenu.downloadJSON'),
        type: 'button',
        click: (meta) => exportAsJson(meta),
      },
      {
        icon: 'description',
        type: 'button',
        name: 'export_csv',
        label: translator.instant('content.exam.actionMenu.downloadCSV'),
        click: (meta) => {
          panelService.exportItemAsCSV(quizPanel, meta.row.id);
        },
      },
    ],
    activeActionNames: ['edit', 'stats', 'delete'],
    activeColumnNames: ['id', 'name', 'num_questions'],
    columns: [
      {
        label: 'ID',
        name: 'id',
        sortable: true,
        type: 'number',
        width: '64px',
        click: (meta) =>
          router.navigateByUrl(`/cms/content/${meta.row.id}/questions`),
        filter: (value) => {
          return {
            id: Number(value),
          };
        },
      },
      {
        click: (meta) =>
          router.navigateByUrl(`/cms/content/${meta.row.id}/questions`),
        label: 'quiz.table.name',
        name: 'name',
        sortable: true,
        primary: true,
        filter: (value) => {
          return {
            name: value,
          };
        },
      },
      {
        label: 'quiz.table.questions',
        name: 'num_questions',
        sortable: true,
        width: '80px',
        click: (meta) =>
          router.navigateByUrl(`/cms/content/${meta.row.id}/questions`),
        filter: (value) => {
          return {
            num_questions: Number(value),
          };
        },
      },
    ],
    createButton: {
      options: [
        {
          text: translator.instant('content.quiz.addMenu.createNewQuiz'),
          icon: 'add',
          click: async (dataSource) => {
            await editService.open(QuizEditConfigService);
            dataSource?.refresh();
          },
        },
      ],
    },
    fetch: (queryParams?: ListQuery<QuizModel>) => {
      const updateSchema = schemas.QuizModel.partial();
      const sentryInfo = {
        title: `Error while fetching quiz list`,
        file: 'quiz-list.component.ts - fetch quizzes',
        api: 'queryQuiz',
      };
      const { isInvalidSchema } = validateSchema(
        updateSchema,
        queryParams.filters,
        matSnackBar,
        sentryInfo
      );
      if (isInvalidSchema) {
        return of(null);
      }
      return quizzes.queryQuiz({ body: queryParams as unknown as QuizQuery });
    },
    query: {
      initial: {
        sorting: [
          {
            option: 'id',
            direction: 'DESC',
          },
        ],
      },
      searchColumnName: 'name',
    },
    name: 'quizList',
    title: 'Quiz',
  };

  async function convertQuizToEvent(
    quiz: QuizModel,
    quizzesService: QuizzesService,
    eventsService: LiveEventsService,
    matSnackBar: MatSnackBar,
    translator: TranslateService,
    router: Router
  ) {
    try {
      const questions = await quizzesService
        .getQuestionsForQuiz({
          id: quiz.id,
        })
        .toPromise();
      const event = await eventsService
        .createLiveEvent({
          body: {
            name: quiz.name,
          } as LiveEvent,
          source: SourceType.QUIZ,
        })
        .toPromise();
      await eventsService
        .linkQuestionToEvent({
          id: event.id,
          body: questions.map((q) => ({ id: q.id } as QuestionModel)),
        })
        .toPromise();
      matSnackBar.open(translator.instant('content.quiz.convertedEvent'));
      router.navigate(['/cms/live-events', event.id]);
    } catch (err) {
      console.log(err);
      matSnackBar.open(
        translator.instant('content.quiz.errorWhileConvertingEvent')
      );
    }
  }

  async function convertToExam(
    quiz: QuizModel,
    quizes: QuizzesService,
    exams: ExamsService,
    matSnackBar,
    translator,
    router: Router
  ) {
    matSnackBar.open(translator.instant('content.quiz.convertingExam'));
    const exam = {
      name: quiz.name,
      scoring_schema: 'DEFAULT',
    };

    try {
      const createdExam = await firstValueFrom(
        exams.createExam({ body: exam as Exam, source: SourceType.QUIZ })
      );
      await linkQuestions(
        quizes,
        quiz.id,
        createdExam.id,
        exams,
        matSnackBar,
        translator
      );
      router.navigate(['/cms/exams', createdExam.id]);
    } catch (error) {
      matSnackBar.open(translator.instant('content.quiz.convertExamError'));
    }
  }

  async function linkQuestions(
    quizes: QuizzesService,
    quizId: number,
    examId: number,
    exams: ExamsService,
    matSnackBar,
    translator
  ) {
    matSnackBar.open(translator.instant('content.quiz.linkingQuestion'));
    try {
      const questions = await firstValueFrom(
        quizes.getQuestionsForQuiz({ embed_answers: false, id: quizId })
      );
      const questionIds = questions.map((q) => ({ id: q.id } as QuestionModel));
      await firstValueFrom(
        exams.linkQuestionToExam({ id: examId, body: questionIds })
      );
      matSnackBar.open(translator.instant('content.quiz.converrtedExam'));
    } catch (error) {
      matSnackBar.open(
        translator.instant('content.quiz.linkingQuestionsError')
      );
    }
  }

  if (showMultiselect) {
    ret.multiselectActions = [
      {
        icon: 'delete',
        name: 'question.tooltip.delete',
        type: 'button',
        click: (meta) => {
          return new Promise((resolve) => {
            dialog
              .confirmDelete(
                null,
                translator.instant('common.message.deleteMulti', {
                  count: meta.rows.length,
                })
              )
              .pipe(filter((isConfirmed) => isConfirmed))
              .subscribe(async () => {
                await multiSelectAction(
                  meta.rows,
                  async (item: any) => {
                    await quizzes.deleteQuiz({ id: item.id }).toPromise();
                    return true;
                  },
                  `${translator.instant(
                    'common.label.deleting'
                  )} ${translator.instant('common.label.quiz')}`,
                  matDialog
                );
                meta.dataSource.refresh();
                utilityService?.triggerRefresh();
                resolve();
              });
          });
        },
      },
    ];
  }

  return ret;
}
